import React, { useEffect, useRef, useState } from "react";
import { Map, useMRE, type LatLng } from "@strava/feature-global-wasm-mre";
import type { CameraInteractionListener } from "@strava/mre";

import Beacon from "./Beacon";
import { isFinished } from "./blipClassName";
import { flyCameraTo, isValidLatLng, panCameraTo } from "./cameraHelpers";

// After a recenter click, suppress auto-follow's translateTo for this many
// ms so the in-flight flyToWithZoomLevel can settle (incl. terrain-loading
// camera adjustment) without being clobbered by the next polling update.
const RECENTER_FLY_GRACE_MS = 1500;

// Smooth-pan-and-zoom factor for the recenter button. Matches the feel of
// the legacy Leaflet setView animation — visible motion, not a snap. Same
// value Map.Bounds uses for animateSpeed="snappy".
const RECENTER_DURATION_FACTOR = 0.3;

interface BeaconMapContentProps {
  beaconId: string;
  livePolyline: LatLng[];
  currentPosition: LatLng | null;
  status: number;
  autoCenter: boolean;
  onUserInteraction: () => void;
  recenterTrigger: number;
  initialFlyZoomLevel: number | null;
}

const BeaconMapContent = ({
  beaconId,
  livePolyline,
  currentPosition,
  status,
  autoCenter,
  onUserInteraction,
  recenterTrigger,
  initialFlyZoomLevel,
}: BeaconMapContentProps) => {
  const { terrainEngine, destroyRef } = useMRE();
  const prevPositionRef = useRef<LatLng | null>(null);
  const hasPositionChangedRef = useRef(false);
  const hasFlownInitialRef = useRef(false);
  // Bumped on every hidden→visible transition. Baked into the Map.Route /
  // Map.Activity `key` so the polyline component remounts: its cleanup calls
  // removeRoutes/removeActivity and the new effect re-fires displayRoute /
  // displayActivity2 with the current polyline.
  //
  // We noticed that when the tab returns to the foreground after being
  // backgrounded, points which should have arrived while the tab was hidden
  // are missing — leaving a straight line across the map. Reloading the full
  // line on each hidden→visible transition works around it. Root cause is
  // not pinned down: it could be missed/throttled polls, MRE state that
  // wasn't applied while the engine was RAF-paused, or both. Map.tsx uses
  // the same pattern to re-fire flyToBounds on visibilitychange.
  const [redrawTrigger, setRedrawTrigger] = useState(0);
  // The auto-follow effect (further below) needs to read the latest
  // autoCenter value, but autoCenter must NOT be in its dependency array.
  //
  // Why: clicking the recenter button does setAutoCenter(true) +
  // setRecenterTrigger(n+1) in the same render. Two effects fire on the
  // resulting re-render: the recenter effect (calls flyToWithZoomLevel)
  // and the auto-follow effect (calls translateTo). If autoCenter were a
  // dep, the false→true flip would re-fire auto-follow on top of the
  // recenter, and translateTo would override the in-flight fly — zoom
  // wouldn't reset. Auto-follow should only fire on new polling data, not
  // on autoCenter transitions, so we mirror autoCenter into a ref.
  const autoCenterRef = useRef(autoCenter);
  useEffect(() => {
    autoCenterRef.current = autoCenter;
  }, [autoCenter]);
  // Timestamp of the last recenter fly. Auto-follow's translateTo skips for
  // RECENTER_FLY_GRACE_MS after a click so the fly can settle without the
  // next polling update overriding the freshly-set zoom.
  const lastRecenterAtRef = useRef(0);

  // Force the polyline component to remount on every hidden→visible
  // transition. See redrawTrigger above for context.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setRedrawTrigger((n) => n + 1);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Initial camera placement for live activity. Runs once when terrainEngine
  // becomes available. Uses FOLLOW_MARGINS (via flyCameraTo) so camera state
  // matches what subsequent translateTo calls expect — no zoom shift when the
  // first follow update or recenter fires.
  useEffect(() => {
    if (
      hasFlownInitialRef.current ||
      initialFlyZoomLevel === null ||
      !terrainEngine ||
      !isValidLatLng(currentPosition)
    ) {
      return;
    }
    hasFlownInitialRef.current = true;
    flyCameraTo(terrainEngine.getCamera(), currentPosition, initialFlyZoomLevel);
  }, [terrainEngine, initialFlyZoomLevel, currentPosition]);

  useEffect(() => {
    if (!terrainEngine || !destroyRef.current) return undefined;

    const listener: CameraInteractionListener = {
      onInteractionStart: () => {
        onUserInteraction();
      },
      onInteractionEnd: () => {},
      onTiltInteractionPrevented: () => {},
    };

    terrainEngine.getCamera().addInteractionListener(listener);
    return () => {
      if (destroyRef.current) {
        terrainEngine.getCamera().removeInteractionListener(listener);
      }
    };
  }, [terrainEngine, destroyRef, onUserInteraction]);

  // Fly to current position when user clicks the recenter button. Mirrors
  // the legacy Mapbox view's behavior (refocusToEndpoint → setView at the
  // default zoom): in addition to recentering, snap zoom back to the
  // initial level so the user gets a predictable view, regardless of how
  // far they've zoomed in/out manually.
  useEffect(() => {
    if (
      recenterTrigger === 0 ||
      initialFlyZoomLevel === null ||
      !terrainEngine ||
      !isValidLatLng(currentPosition)
    ) {
      return;
    }
    lastRecenterAtRef.current = Date.now();
    flyCameraTo(
      terrainEngine.getCamera(),
      currentPosition,
      initialFlyZoomLevel,
      RECENTER_DURATION_FACTOR
    );
    // Intentionally only re-run when the user clicks the recenter button — we
    // don't want to fly every time currentPosition / terrainEngine /
    // initialFlyZoomLevel identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterTrigger]);

  // Track when the position changes (new polling data) — we only follow after
  // the first position change, letting the initial flyTo (or, for finished
  // activities, Map.Bounds) settle the camera first.
  useEffect(() => {
    if (!currentPosition) return;
    const prev = prevPositionRef.current;
    if (prev && (currentPosition[0] !== prev[0] || currentPosition[1] !== prev[1])) {
      hasPositionChangedRef.current = true;
    }
    prevPositionRef.current = currentPosition;
  }, [currentPosition]);

  useEffect(() => {
    // Don't override the initial camera placement — only follow once new
    // polling data has arrived. The initial flyTo / Map.Bounds framing
    // remains the default view. autoCenter is read via ref so flipping it
    // on doesn't re-fire this effect; that flip is the recenter button's
    // job, which calls flyTo separately.
    if (
      !autoCenterRef.current ||
      !terrainEngine ||
      !isValidLatLng(currentPosition) ||
      !hasPositionChangedRef.current
    ) {
      return;
    }
    // Skip translateTo in the grace window after a recenter click so the
    // in-flight flyToWithZoomLevel can settle (incl. terrain-driven camera
    // adjustment) without translateTo "preserving" the wrong camera state.
    if (Date.now() - lastRecenterAtRef.current < RECENTER_FLY_GRACE_MS) {
      return;
    }
    panCameraTo(terrainEngine.getCamera(), currentPosition);
  }, [currentPosition, terrainEngine]);

  const startPoint = livePolyline.length > 0 ? livePolyline[0] : null;

  if (isFinished(status)) {
    return (
      <Map.Activity
        key={`activity-${redrawTrigger}`}
        id={`beacon-activity-${beaconId}`}
        polyline={livePolyline}
      />
    );
  }

  return (
    <>
      <Map.Route
        key={`route-${redrawTrigger}`}
        id={`beacon-route-${beaconId}`}
        polyline={livePolyline}
        isSelected={true}
        disableSelectedEndpoints={true}
      />

      {startPoint && <Map.Marker latLng={startPoint} type="route-start" />}

      {currentPosition && <Beacon latLng={currentPosition} status={status} />}
    </>
  );
};

export default BeaconMapContent;
